import React, { useEffect, useState, useMemo, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { getMoment, showDateNoTime } from '@libs/NwMoment';

import { useLayoutContext } from '@contexts/LayoutContext';
import { ListContext } from '@contexts/ListContext';
import { useStateValue } from '@contexts/StateProvider';
import { useUserPermission } from '@contexts/UserContext';

import { modelFullName } from '@hooks/API/Contact/UseContactsList';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import ModelDetailsAge from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsAge';

import ModelDrawerAccounting from '@components/ContactDetails/ModelDetails/Drawer/ModelDrawerAccounting';
import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import { Loading } from '@components/Gui/Loading';
import NwIcon, { NwGenderIcon } from '@components/Gui/NwIcon';
import { NwTag } from '@components/Gui/NwTag';
import { ModelCover } from '@components/ModelCover';

import { NameCell, StyledModelsTable, ModelCoverWrapper } from '../ModelsTable/ModelsTableUI';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ModelAgenciesContainer = styled.div`
    font-weight: ${(props) => (props.$isBold ? 'bold' : 'normal')};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    text-transform: uppercase;

    .icon-container {
        width: 0.75rem;
        text-align: center;
    }

    p {
        margin: 0;
    }

    small {
        font-weight: normal;
        font-size: 0.75rem;
    }
`;

const Wrapper = styled.div`
    .storage-row {
        td {
            background-color: ${(props) => props.theme.LighterBorderColor};
        }
    }

    .not-approved-row {
        td {
            opacity: 0.7;
            background-color: ${(props) => props.theme.WarningColorLight};
        }
        &:hover td {
            background-color: ${(props) => props.theme.WarningColorLight};
        }
    }

    .id-column {
        font-size: ${(props) => props.theme.FontSizeLg};
        font-weight: bold;
        text-align: center;
    }
    .id-column-storage {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: ${(props) => props.theme.FontSizeLg};
        font-weight: bold;
        color: ${(props) => props.theme.DangerColor};
    }

    .legal-name {
        font-size: 1rem;
        line-height: 1.4em;
        margin: 0;
        color: ${(props) => props.theme.TextColor};
        font-weight: normal;
        text-transform: capitalize;
    }

    .details-column {
        text-transform: uppercase;

        .details-column-row {
            font-size: ${(props) => props.theme.FontSizeSm};
            display: flex;
            justify-content: flex-start;
            align-items: center;
            gap: 1rem;
            padding-bottom: 0.5rem;
        }

        p {
            margin: 0;
            line-height: 1.5em;
            font-size: ${(props) => props.theme.FontSizeSm};
            span {
                color: ${(props) => props.theme.TextColorMuted};
                padding-right: 0.5rem;
            }
            strong {
                color: ${(props) => props.theme.TextColor};
                font-weight: normal;
            }
        }
    }
`;

const ModelDetailsBlock = ({ label, value, isDate, isCountry, isInvoiceType }) => {
    const { GetCountry, cachedList } = useContext(ListContext);
    const invoicetypedefinitions = cachedList.taxmethods.items.ModelInvoiceTypes;

    const getInvoiceTypeName = (code) => {
        const invoiceType = invoicetypedefinitions.find((invtype) => invtype.Code === code);
        return invoiceType ? invoiceType.Name : code;
    };

    const getCountryName = (code) => {
        const country = GetCountry(code);
        return country ? country.Name : code;
    };

    if (!value) return null;

    let content = value;

    if (isDate) {
        content = showDateNoTime(value);
    }

    if (isCountry) {
        content = getCountryName(value);
    }

    if (isInvoiceType) {
        content = getInvoiceTypeName(value);
    }

    return (
        <p>
            <span>{label}:</span>
            <strong>{content}</strong>
        </p>
    );
};

const ModelsTableAccounting = ({ list }) => {
    const navigate = useNavigate();
    const configurations = useConfigurations();
    const { selection, context, layout, actions } = useContactsSelector();
    const { activeSet, listType } = selection;
    const { area, scope } = context;
    const { itemClickAction, multipleSelectionActive, selectedItemsIDs } = actions;

    //const showID = quickFilters && quickFilters.sort && quickFilters.sort === 'ID';
    const showID = true;

    const cs_dispatch = useContactsSelectorDispatch();

    // Filter and order Main Features depending on User Metrics Config
    const [pagedList, setPagedList] = useState([]);

    const layoutContext = useLayoutContext();
    const { breakpoints, isMobile } = layoutContext;

    const [isOpenDrawer, setIsOpenDrawer] = useState(false);
    const [drawerId, setDrawerId] = useState();
    const [{ areaContext }] = useStateValue();
    const [loadMore, setLoadMore] = useState(false);

    const accessManagement = useUserPermission('AccessManagement');

    const loader = useRef(null);

    const handleObserver = useCallback((entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
            setLoadMore(true);
        }
    }, []);

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: '10px',
            threshold: 0,
        };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    const pageCardCount = 50;

    useEffect(() => {
        setPagedList(list.slice(0, pageCardCount));
    }, [list]);

    useEffect(() => {
        if (loadMore) {
            if (list.length > pagedList.length) {
                const newShowedList = list.slice(pagedList.length, pagedList.length + pageCardCount);
                setPagedList([...pagedList, ...newShowedList]);
            }
            setLoadMore(false);
        }
    }, [loadMore]);

    const dataSource = useMemo(() => {
        const pds = pagedList.map((model) => ({
            key: model.ID,
            FullName: modelFullName(model),
            ...model,
        }));
        return pds;
    }, [pagedList]);

    const handleOpenDrawer = (modelId) => {
        setDrawerId(modelId);
        setIsOpenDrawer(true);
    };

    const handleCloseDrawer = () => {
        setIsOpenDrawer(false);
        setDrawerId();
    };

    const handleClick = (key) => {
        if (multipleSelectionActive) {
            cs_dispatch({ type: 'ToggleSelectedItem', ID: key });
            return;
        }
        if (isMobile) {
            handleOpenDrawer(key);
            return;
        }
        if (itemClickAction === 'preview-drawer') {
            handleOpenDrawer(key);
            return;
        }
        if (itemClickAction === 'navigate') {
            window.scrollTo(0, 0);
            if (listType && activeSet) {
                navigate(`/model/${key}/${listType}/${activeSet}`);
            } else {
                navigate(`/model/${key}`);
            }
            return;
        }
    };

    const getContactOrDefaultModelCommission = (model) => {
        if (model.Commission !== null && model.Commission !== undefined) {
            return `${model.Commission}%`;
        }
        return `${configurations.defaultAgencyCommissionPerc}% [default]`;
    };

    const IDColumn = {
        title: 'ID',
        key: 'ID',
        width: '6rem',
        render: (record) => {
            if (!record.StorageBooking) {
                return (
                    <div className="id-column">
                        {record.ID}
                        {/* {!isContactApproved(record.ID) && (
                            <NwIcon
                                icon={byPrefixAndName.fasl['triangle-exclamation']}
                                style={{
                                    color: 'var(--warning-color)',
                                    marginLeft: '0.5rem',
                                    fontSize: '0.8rem',
                                }}
                                title="Not approved"
                            />
                        )} */}
                    </div>
                );
            } else {
                return (
                    <div className="id-column-storage">
                        {record.ID}
                        <NwIcon icon={byPrefixAndName.fasl['box-archive']} />
                    </div>
                );
            }
        },
        onCell: (record) => {
            return {
                onClick: (event) => {
                    handleClick(record.key);
                },
            };
        },
    };

    const thumbnailColumn = {
        title: 'Image',
        key: 'CoverGuid',
        width: isMobile ? '4rem' : '7rem',
        className: 'cover-image',
        render: (record) => {
            const selected = selectedItemsIDs.find((modelId) => modelId === record.ID);
            return (
                <ModelCoverWrapper selected={multipleSelectionActive && selected}>
                    <ModelCover maxWidth={60} model={record} classN="model-cover-small" />
                </ModelCoverWrapper>
            );
        },
        onCell: (record, rowIndex) => {
            return {
                onClick: (event) => {
                    handleClick(record.key);
                },
            };
        },
    };

    const nameColumn = {
        title: 'Name',
        key: 'FullName',
        width: '18rem',
        render: (record) => {
            return (
                <NameCell $mobileView={isMobile}>
                    <div className="name">
                        <h1>{record.FullName.toLowerCase()}</h1>
                        {/* {record.AL && <ContactAlertPopover contact={record} />} */}
                    </div>
                    {(record.LegalName || record.LegalSurname) &&
                        record.LegalName + ' ' + record.LegalSurname !== record.FullName && (
                            <h2 className="legal-name">
                                {record.LegalName.toLowerCase()} {record.LegalSurname.toLowerCase()}
                            </h2>
                        )}
                    <div className="detail">
                        {record.MotherAgencyID ? (
                            <ModelAgenciesContainer>
                                <div className="icon-container">
                                    <NwIcon icon={byPrefixAndName.fasl['globe']} />
                                </div>
                                <p>{record.MotherAgencyName}</p>
                            </ModelAgenciesContainer>
                        ) : record.Represented ? (
                            <ModelAgenciesContainer $isBold>
                                <div className="icon-container">
                                    <NwIcon icon={byPrefixAndName.fasl['medal']} />
                                </div>
                                <p>represented</p>
                            </ModelAgenciesContainer>
                        ) : (
                            <ModelAgenciesContainer>
                                <div className="icon-container">
                                    <NwIcon icon={byPrefixAndName.fasl['globe']} />
                                </div>
                                <p>no mother ag.</p>
                            </ModelAgenciesContainer>
                        )}
                    </div>
                </NameCell>
            );
        },
        onCell: (record) => {
            return {
                onClick: (event) => {
                    handleClick(record.key);
                },
            };
        },
    };

    const secondColumn = {
        title: 'Details',
        key: 'BirthDate',
        //width: "100%",
        width: '100%',
        render: (record) => {
            return (
                <div className="details-column">
                    <div className="details-column-row">
                        {record.BirthDate && (
                            <span>
                                <ModelDetailsAge birthday={record.BirthDate} nofixedspace small />
                            </span>
                        )}
                        <span>
                            <NwGenderIcon gender={`${record.GenderName}`} /> {record.GenderName}
                        </span>
                    </div>
                    <ModelDetailsBlock label="Nationality" value={record.NationalityCode} isCountry />
                    <ModelDetailsBlock label="Birthplace" value={record.BirthPlace} />
                    <ModelDetailsBlock label="Birthdate" value={record.BirthDate} isDate />
                    <ModelDetailsBlock label="In Agency Since" value={record.InAgencyFrom} isDate />
                </div>
            );
        },
        onCell: (record) => {
            return {
                onClick: (event) => {
                    handleClick(record.key);
                },
            };
        },
    };

    const thirdColumn = {
        title: 'Billing',
        key: 'BillingInfos',
        //width: "100%",
        width: '100%',
        render: (record) => {
            const billingInfos = record.BillingInfos;
            let billingInfo = null;
            if (record.DefaultBillingInfoID) {
                billingInfo = billingInfos.find((info) => info.ID === record.DefaultBillingInfoID);
            } else {
                billingInfo = billingInfos[0];
            }

            return (
                <div className="details-column">
                    <div className="details-column-row">
                        {/* <ModelDetailsBlock label="Commission" value={`${record.Commission}%`} isCountry /> */}
                        <ModelDetailsBlock label="Commission" value={`${getContactOrDefaultModelCommission(record)}`} />
                    </div>
                    {billingInfo && (
                        <>
                            <ModelDetailsBlock label="Invoice Type" value={billingInfo.InvoiceType} isInvoiceType />
                            <ModelDetailsBlock label="Billing Country" value={billingInfo.Country} isCountry />
                            {billingInfo.VatNumber && (
                                <ModelDetailsBlock label="VAT Number" value={billingInfo.VatNumber} />
                            )}
                            {billingInfo.FiscalID && (
                                <ModelDetailsBlock label="Fiscal ID" value={billingInfo.FiscalID} />
                            )}
                        </>
                    )}
                </div>
            );
        },
        onCell: (record) => {
            return {
                onClick: (event) => {
                    handleClick(record.key);
                },
            };
        },
    };

    const columns = [];

    columns.push(IDColumn);
    columns.push(thumbnailColumn);
    columns.push(nameColumn);
    columns.push(secondColumn);
    columns.push(thirdColumn);

    const rowSelection = {
        selectedRowKeys: selectedItemsIDs,
        onChange: (selectedRowKeys) => {
            cs_dispatch({ type: 'UpdateSelectedItems', newlist: selectedRowKeys });
        },
        getCheckboxProps: (record) => ({
            disabled: !record.Approved || !record.BillingInfos || record.BillingInfos.length === 0,
            title: !record.Approved ? 'This model is not approved and cannot be selected' : '',
        }),
    };

    return (
        <Wrapper>
            <StyledModelsTable
                breakpoints={breakpoints}
                rowSelection={multipleSelectionActive && breakpoints > 3 ? rowSelection : null}
                dataSource={dataSource}
                className="models-table"
                columns={columns}
                size="middle"
                pagination={false}
                showHeader={false}
                tableLayout="fixed"
                rowClassName={(record) => {
                    let className = '';
                    if (record.StorageBooking) {
                        className += 'storage-row ';
                    }
                    if (!record.Approved) {
                        className += 'not-approved-row ';
                    }
                    return className;
                }}
            />
            {isOpenDrawer && <ModelDrawerAccounting modelID={drawerId} onClose={handleCloseDrawer} />}
            <div ref={loader}>{loadMore && <Loading textBlack small />}</div>
        </Wrapper>
    );
};

export default ModelsTableAccounting;
